import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ui } from "@/lib/ui";
import { ArrowLeft, Plus, Trash2, Save, Loader2 } from "lucide-react";

const TABS = ["Basics", "Services", "Palette & Fonts", "Content", "Images"];

function ListEditor({ items, setItems, fields, testid, addLabel = "Add" }) {
  const add = () => setItems([...(items || []), Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const upd = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const rem = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-2 items-start bg-slate-950/50 border border-slate-800 rounded-lg p-2">
            {fields.map((f) => (
              <input key={f.key} data-testid={`${testid}-${i}-${f.key}`} className={`${ui.input} ${f.w || ""}`} placeholder={f.label} value={it[f.key] ?? ""} onChange={(e) => upd(i, f.key, e.target.value)} />
            ))}
            <button data-testid={`${testid}-remove-${i}`} onClick={() => rem(i)} className="p-2.5 text-slate-500 hover:text-rose-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button data-testid={`${testid}-add`} onClick={add} className={`${ui.btnGhost} mt-2`}><Plus className="w-4 h-4" /> {addLabel}</button>
    </div>
  );
}

export default function ProfileEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("Basics");
  const [saving, setSaving] = useState(false);
  const [kw, setKw] = useState("");

  useEffect(() => {
    api.get(`/profiles/${id}`).then(({ data }) => { setP(data); setKw((data.keyword_phrases || []).join(", ")); })
      .catch(() => toast.error("Failed to load profile"));
  }, [id]);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...p, keyword_phrases: kw.split(",").map((x) => x.trim()).filter(Boolean) };
      await api.put(`/profiles/${id}`, payload);
      toast.success("Profile saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const addImage = async (url, subject) => {
    if (!url || !subject) return;
    const { data } = await api.post(`/profiles/${id}/images`, { url, subject });
    set("image_library", [...(p.image_library || []), data]);
  };
  const delImage = async (imgId) => {
    await api.delete(`/profiles/${id}/images/${imgId}`);
    set("image_library", p.image_library.filter((x) => x.id !== imgId));
  };

  if (!p) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="animate-fade-up">
      <Link to="/profiles" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-4"><ArrowLeft className="w-4 h-4" /> Category Profiles</Link>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className={ui.pageHeader}>{p.category_name}</h1>
        <button data-testid="save-profile" onClick={save} disabled={saving} className={ui.btnPrimary}>{saving ? <Loader2 className="w-4 h-4 spin" /> : <Save className="w-4 h-4" />} Save Profile</button>
      </div>

      <div className="flex gap-2 border-b border-slate-800 mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium font-h whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t ? "border-indigo-500 text-indigo-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{t}</button>
        ))}
      </div>

      <div className={`${ui.card} p-6`}>
        {tab === "Basics" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={ui.label}>Category Name</label><input className={ui.input} value={p.category_name} onChange={(e) => set("category_name", e.target.value)} /></div>
            <div><label className={ui.label}>Schema additionalType</label><input className={ui.input} value={p.additional_type || ""} onChange={(e) => set("additional_type", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className={ui.label}>Schema Keywords</label><input className={ui.input} value={p.schema_keywords || ""} onChange={(e) => set("schema_keywords", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className={ui.label}>Keyword Phrases (comma separated)</label><textarea className={ui.textarea} value={kw} onChange={(e) => setKw(e.target.value)} /></div>
          </div>
        )}

        {tab === "Services" && (
          <>
            <p className={`${ui.muted} mb-3`}>Each service: display name, on-page anchor id, and guidance for the writer.</p>
            <ListEditor testid="services-editor" items={p.services} setItems={(v) => set("services", v)} addLabel="Add service"
              fields={[{ key: "name", label: "Name", w: "w-52" }, { key: "anchor", label: "anchor-id", w: "w-44" }, { key: "guidance", label: "Writing guidance" }]} />
          </>
        )}

        {tab === "Palette & Fonts" && (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={ui.label}>Heading Font</label><input className={ui.input} value={p.heading_font} onChange={(e) => set("heading_font", e.target.value)} /></div>
              <div><label className={ui.label}>Body Font</label><input className={ui.input} value={p.body_font} onChange={(e) => set("body_font", e.target.value)} /></div>
            </div>
            <div>
              <label className={ui.label}>Palette (name · hex · role)</label>
              <p className="text-xs text-slate-500 mb-2">Roles: primary_button, accent, accent_2, dark, cream, offwhite, text, heading</p>
              <div className="space-y-2" data-testid="palette-editor">
                {(p.palette || []).map((c, i) => (
                  <div key={i} className="flex gap-2 items-center bg-slate-950/50 border border-slate-800 rounded-lg p-2">
                    <input type="color" value={c.hex} onChange={(e) => set("palette", p.palette.map((x, idx) => idx === i ? { ...x, hex: e.target.value } : x))} className="w-10 h-9 rounded bg-transparent border border-slate-700 cursor-pointer" />
                    <input className={`${ui.input} w-44`} placeholder="Name" value={c.name} onChange={(e) => set("palette", p.palette.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} />
                    <input className={`${ui.input} w-32`} placeholder="#hex" value={c.hex} onChange={(e) => set("palette", p.palette.map((x, idx) => idx === i ? { ...x, hex: e.target.value } : x))} />
                    <input className={`${ui.input} flex-1`} placeholder="role" value={c.role} onChange={(e) => set("palette", p.palette.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x))} />
                    <button onClick={() => set("palette", p.palette.filter((_, idx) => idx !== i))} className="p-2.5 text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => set("palette", [...(p.palette || []), { name: "", hex: "#888888", role: "" }])} className={`${ui.btnGhost} mt-1`}><Plus className="w-4 h-4" /> Add color</button>
              </div>
            </div>
          </div>
        )}

        {tab === "Content" && (
          <div className="space-y-8">
            <div><h3 className={`${ui.cardTitle} mb-2`}>Trust Strip Items</h3>
              <ListEditor testid="trust-editor" items={p.trust_items} setItems={(v) => set("trust_items", v)} addLabel="Add trust item"
                fields={[{ key: "label", label: "Label" }, { key: "icon", label: "fa-solid fa-...", w: "w-56" }]} /></div>
            <div><h3 className={`${ui.cardTitle} mb-2`}>Why Choose Us</h3>
              <ListEditor testid="why-editor" items={p.why_choose_us} setItems={(v) => set("why_choose_us", v)} addLabel="Add reason"
                fields={[{ key: "title", label: "Title" }, { key: "icon", label: "fa-solid fa-...", w: "w-56" }]} /></div>
            <div><h3 className={`${ui.cardTitle} mb-2`}>FAQ Topics</h3>
              <ListEditor testid="faq-editor" items={p.faq_topics} setItems={(v) => set("faq_topics", v)} addLabel="Add FAQ topic"
                fields={[{ key: "question", label: "Question", w: "w-72" }, { key: "guidance", label: "Answer guidance" }]} /></div>
          </div>
        )}

        {tab === "Images" && (
          <div>
            <p className={`${ui.muted} mb-3`}>Image library tagged by subject (hero, exterior, interior, equipment, technician…).</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" data-testid="image-library">
              {(p.image_library || []).map((img) => (
                <div key={img.id} className="relative rounded-lg overflow-hidden border border-slate-800 group">
                  <img src={img.url} alt={img.subject} className="w-full h-32 object-cover" />
                  <span className="absolute top-2 left-2 text-[10px] font-mono uppercase bg-slate-950/80 text-indigo-400 px-2 py-0.5 rounded">{img.subject}</span>
                  <button onClick={() => delImage(img.id)} className="absolute top-2 right-2 p-1.5 bg-slate-950/80 rounded text-slate-300 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <AddImage onAdd={addImage} />
          </div>
        )}
      </div>
    </div>
  );
}

function AddImage({ onAdd }) {
  const [url, setUrl] = useState("");
  const [subject, setSubject] = useState("gallery");
  return (
    <div className="flex gap-2 flex-wrap items-end bg-slate-950/50 border border-slate-800 rounded-lg p-3" data-testid="add-image">
      <div className="flex-1 min-w-[240px]"><label className={ui.label}>Image URL</label><input className={ui.input} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" /></div>
      <div><label className={ui.label}>Tag (optional)</label><input className={`${ui.input} w-40`} value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
      <button className={ui.btnGhost} onClick={() => { onAdd(url, subject); setUrl(""); }}><Plus className="w-4 h-4" /> Add</button>
    </div>
  );
}
